import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Input,
  Select,
  Row,
  Col,
  Statistic,
  Modal,
  Form,
  Rate,
  Progress,
  Avatar,
  Tabs,
  List,
  Badge,
  Tooltip,
  Dropdown,
  Menu,
  Descriptions,
} from 'antd';
import { PersistentStorage } from '../../../../utils/persistentStorage';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  StarOutlined,
  TruckOutlined,
  DollarOutlined,
  ShoppingCartOutlined,
  MoreOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import type { Vendor, VendorStatus, VendorType, VendorCategory } from '../../types';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

interface VendorManagementProps {
  vendors: Vendor[];
  onVendorCreate: (vendor: any) => void;
  onVendorUpdate: (id: string, data: any) => void;
  onVendorDelete: (id: string) => void;
}

export const VendorManagement: React.FC<VendorManagementProps> = ({
  vendors,
  onVendorCreate,
  onVendorUpdate,
  onVendorDelete,
}) => {
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<VendorStatus | undefined>();
  const [typeFilter, setTypeFilter] = useState<VendorType | undefined>();
  const [categoryFilter, setCategoryFilter] = useState<VendorCategory | undefined>();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  // Mock data for demonstration
  const mockVendors: Vendor[] = [
    {
      id: '1',
      code: 'V001',
      name: 'ABC Supplies Inc.',
      type: 'supplier',
      category: 'office_supplies',
      status: 'active',
      contactInfo: {
        primaryContact: {
          name: 'John Smith',
          title: 'Sales Manager',
          email: 'john@abcsupplies.com',
          phone: '+1-555-0123',
        },
        alternateContact: {
          name: 'Sarah Johnson',
          title: 'Account Manager',
          email: 'sarah@abcsupplies.com',
          phone: '+1-555-0124',
        },
      },
      address: {
        billing: {
          street: '123 Business St',
          city: 'New York',
          state: 'NY',
          postalCode: '10001',
          country: 'USA',
        },
        shipping: {
          street: '456 Warehouse Ave',
          city: 'New York',
          state: 'NY',
          postalCode: '10002',
          country: 'USA',
        },
      },
      paymentTerms: {
        termCode: 'NET30',
        description: 'Net 30 days',
        daysNet: 30,
        currency: 'USD',
        paymentMethod: 'bank_transfer',
      },
      taxInfo: {
        taxId: '12-3456789',
        taxExempt: false,
        taxCategory: 'standard',
      },
      documents: [
        {
          id: '1',
          type: 'contract',
          name: 'Service Agreement 2024',
          url: '/documents/contract-v001.pdf',
          uploadedAt: new Date(),
          status: 'valid',
        },
      ],
      rating: {
        overall: 4.5,
        quality: 4.0,
        delivery: 5.0,
        service: 4.5,
        price: 4.0,
        totalOrders: 25,
        onTimeDelivery: 95,
        lastEvaluated: new Date(),
      },
      certifications: [
        {
          id: '1',
          name: 'ISO 9001:2015',
          issuingBody: 'ISO',
          issueDate: new Date('2023-01-01'),
          expiryDate: new Date('2026-01-01'),
          certificateNumber: 'ISO-9001-2023-001',
          status: 'active',
        },
      ],
      isActive: true,
      createdAt: new Date('2023-01-01'),
      updatedAt: new Date(),
      createdBy: 'admin',
    },
    {
      id: '2',
      code: 'V002',
      name: 'Tech Solutions Ltd.',
      type: 'service_provider',
      category: 'services',
      status: 'active',
      contactInfo: {
        primaryContact: {
          name: 'Mike Brown',
          title: 'Business Development',
          email: 'mike@techsolutions.com',
          phone: '+1-555-0456',
        },
      },
      address: {
        billing: {
          street: '789 Tech Park',
          city: 'San Francisco',
          state: 'CA',
          postalCode: '94105',
          country: 'USA',
        },
      },
      paymentTerms: {
        termCode: 'NET15',
        description: 'Net 15 days',
        daysNet: 15,
        currency: 'USD',
        paymentMethod: 'bank_transfer',
      },
      taxInfo: {
        taxId: '98-7654321',
        taxExempt: false,
        taxCategory: 'standard',
      },
      documents: [],
      rating: {
        overall: 4.2,
        quality: 4.5,
        delivery: 4.0,
        service: 4.5,
        price: 3.8,
        totalOrders: 12,
        onTimeDelivery: 88,
        lastEvaluated: new Date(),
      },
      certifications: [],
      isActive: true,
      createdAt: new Date('2023-06-01'),
      updatedAt: new Date(),
      createdBy: 'admin',
    },
    {
      id: '3',
      code: 'V003',
      name: 'Global Manufacturing Corp',
      type: 'supplier',
      category: 'raw_materials',
      status: 'pending_approval',
      contactInfo: {
        primaryContact: {
          name: 'Lisa Wong',
          title: 'Regional Manager',
          email: 'lisa@globalmanuf.com',
          phone: '+1-555-0789',
        },
      },
      address: {
        billing: {
          street: '321 Industrial Blvd',
          city: 'Detroit',
          state: 'MI',
          postalCode: '48201',
          country: 'USA',
        },
      },
      paymentTerms: {
        termCode: 'NET45',
        description: 'Net 45 days',
        daysNet: 45,
        currency: 'USD',
        paymentMethod: 'check',
      },
      taxInfo: {
        taxId: '45-6789123',
        taxExempt: false,
        taxCategory: 'standard',
      },
      documents: [],
      rating: {
        overall: 0,
        quality: 0,
        delivery: 0,
        service: 0,
        price: 0,
        totalOrders: 0,
        onTimeDelivery: 0,
        lastEvaluated: new Date(),
      },
      certifications: [],
      isActive: false,
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: 'admin',
    },
  ];

  // Load persistent vendors data, fallback to mock data if empty
  const persistentVendors = PersistentStorage.initializeWithMockData('vendors', mockVendors);
  const [filteredData, setFilteredData] = useState(persistentVendors);

  useEffect(() => {
    let filtered = mockVendors;

    if (searchText) {
      filtered = filtered.filter(vendor =>
        vendor.name.toLowerCase().includes(searchText.toLowerCase()) ||
        vendor.code.toLowerCase().includes(searchText.toLowerCase()) ||
        vendor.contactInfo.primaryContact.name.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    if (statusFilter) {
      filtered = filtered.filter(vendor => vendor.status === statusFilter);
    }

    if (typeFilter) {
      filtered = filtered.filter(vendor => vendor.type === typeFilter);
    }

    if (categoryFilter) {
      filtered = filtered.filter(vendor => vendor.category === categoryFilter);
    }

    setFilteredData(filtered);
  }, [searchText, statusFilter, typeFilter, categoryFilter]);

  const getStatusColor = (status: VendorStatus): string => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'pending_approval': return 'warning';
      case 'blacklisted': return 'error';
      case 'under_review': return 'processing';
      default: return 'default';
    }
  };

  const getTypeColor = (type: VendorType): string => {
    switch (type) {
      case 'supplier': return 'blue';
      case 'service_provider': return 'green';
      case 'contractor': return 'orange';
      case 'consultant': return 'purple';
      default: return 'blue';
    }
  };

  const getCategoryColor = (category: VendorCategory): string => {
    switch (category) {
      case 'raw_materials': return 'brown';
      case 'finished_goods': return 'cyan';
      case 'services': return 'lime';
      case 'equipment': return 'gold';
      case 'utilities': return 'volcano';
      case 'maintenance': return 'magenta';
      default: return 'blue';
    }
  };

  const handleAction = (record: Vendor, action: string) => {
    switch (action) {
      case 'view':
        setSelectedVendor(record);
        setIsModalVisible(true);
        break;
      case 'edit':
        onVendorUpdate(record.id, record);
        break;
      case 'delete':
        Modal.confirm({
          title: 'Delete Vendor',
          content: `Are you sure you want to delete vendor ${record.name}?`,
          onOk: () => onVendorDelete(record.id),
        });
        break;
      case 'activate':
        onVendorUpdate(record.id, { ...record, status: 'active' });
        break;
      case 'deactivate':
        onVendorUpdate(record.id, { ...record, status: 'inactive' });
        break;
    }
  };

  const actionMenu = (record: Vendor) => (
    <Menu>
      <Menu.Item key="view" icon={<EyeOutlined />} onClick={() => handleAction(record, 'view')}>
        View Profile
      </Menu.Item>
      <Menu.Item key="edit" icon={<EditOutlined />} onClick={() => handleAction(record, 'edit')}>
        Edit
      </Menu.Item>
      {record.status === 'active' ? (
        <Menu.Item key="deactivate" onClick={() => handleAction(record, 'deactivate')}>
          Deactivate
        </Menu.Item>
      ) : (
        <Menu.Item key="activate" onClick={() => handleAction(record, 'activate')}>
          Activate
        </Menu.Item>
      )}
      <Menu.Divider />
      <Menu.Item key="delete" danger icon={<DeleteOutlined />} onClick={() => handleAction(record, 'delete')}>
        Delete
      </Menu.Item>
    </Menu>
  );

  const columns = [
    {
      title: 'Vendor Code',
      dataIndex: 'code',
      key: 'code',
      width: 120,
      fixed: 'left' as const,
      render: (text: string, record: Vendor) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ color: '#1890ff', cursor: 'pointer' }} onClick={() => handleAction(record, 'view')}>
            {text}
          </Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {new Date(record.createdAt).toLocaleDateString()}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Vendor Information',
      dataIndex: 'name',
      key: 'name',
      width: 250,
      render: (text: string, record: Vendor) => (
        <Space>
          <Avatar size="large" icon={<UserOutlined />} />
          <Space direction="vertical" size={0}>
            <Text strong>{text}</Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.contactInfo.primaryContact.name}
            </Text>
            <Space size={8}>
              <Tooltip title={record.contactInfo.primaryContact.email}>
                <MailOutlined style={{ color: '#1890ff' }} />
              </Tooltip>
              <Tooltip title={record.contactInfo.primaryContact.phone}>
                <PhoneOutlined style={{ color: '#52c41a' }} />
              </Tooltip>
            </Space>
          </Space>
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type: VendorType) => (
        <Tag color={getTypeColor(type)} style={{ textTransform: 'capitalize' }}>
          {type.replace('_', ' ')}
        </Tag>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 130,
      render: (category: VendorCategory) => (
        <Tag color={getCategoryColor(category)} style={{ textTransform: 'capitalize' }}>
          {category.replace('_', ' ')}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status: VendorStatus) => (
        <Badge 
          status={getStatusColor(status) as any} 
          text={
            <span style={{ textTransform: 'capitalize' }}>
              {status.replace('_', ' ')}
            </span>
          } 
        />
      ),
    },
    {
      title: 'Rating',
      dataIndex: 'rating',
      key: 'rating',
      width: 120,
      render: (rating: any) => (
        <Space direction="vertical" size={0}>
          <Rate disabled defaultValue={rating.overall} style={{ fontSize: '14px' }} />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {rating.totalOrders} orders
          </Text>
        </Space>
      ),
    },
    {
      title: 'Performance',
      key: 'performance',
      width: 140,
      render: (_, record: Vendor) => (
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Space justify="space-between" style={{ width: '100%' }}>
            <Text style={{ fontSize: '12px' }}>On-time delivery</Text>
            <Text style={{ fontSize: '12px' }}>{record.rating.onTimeDelivery}%</Text>
          </Space>
          <Progress 
            percent={record.rating.onTimeDelivery} 
            size="small" 
            showInfo={false}
            strokeColor={record.rating.onTimeDelivery >= 90 ? '#52c41a' : record.rating.onTimeDelivery >= 70 ? '#faad14' : '#ff4d4f'}
          />
        </Space>
      ),
    },
    {
      title: 'Payment Terms',
      dataIndex: 'paymentTerms',
      key: 'paymentTerms',
      width: 120,
      render: (terms: any) => (
        <Space direction="vertical" size={0}>
          <Text strong>{terms.termCode}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {terms.daysNet} days
          </Text>
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 60,
      fixed: 'right' as const,
      render: (_, record: Vendor) => (
        <Dropdown overlay={actionMenu(record)} trigger={['click']} placement="bottomRight">
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(selectedRowKeys);
    },
  };

  return (
    <div className="vendor-management">
      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Vendors"
              value={filteredData.length}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active Vendors"
              value={filteredData.filter(v => v.status === 'active').length}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Pending Approval"
              value={filteredData.filter(v => v.status === 'pending_approval').length}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Avg Rating"
              value={filteredData.reduce((sum, v) => sum + v.rating.overall, 0) / filteredData.length || 0}
              prefix={<StarOutlined />}
              precision={1}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Table Card */}
      <Card
        title={<Title level={4}>Vendor Management</Title>}
        extra={
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => onVendorCreate({})}>
              New Vendor
            </Button>
          </Space>
        }
      >
        {/* Filters */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Input
              placeholder="Search vendors..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
              <Option value="pending_approval">Pending Approval</Option>
              <Option value="blacklisted">Blacklisted</Option>
              <Option value="under_review">Under Review</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Type"
              value={typeFilter}
              onChange={setTypeFilter}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="supplier">Supplier</Option>
              <Option value="service_provider">Service Provider</Option>
              <Option value="contractor">Contractor</Option>
              <Option value="consultant">Consultant</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Category"
              value={categoryFilter}
              onChange={setCategoryFilter}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="raw_materials">Raw Materials</Option>
              <Option value="finished_goods">Finished Goods</Option>
              <Option value="services">Services</Option>
              <Option value="equipment">Equipment</Option>
              <Option value="utilities">Utilities</Option>
              <Option value="maintenance">Maintenance</Option>
            </Select>
          </Col>
        </Row>

        {/* Bulk Actions */}
        {selectedRowKeys.length > 0 && (
          <div style={{ marginBottom: 16, padding: 8, background: '#f0f2f5', borderRadius: 4 }}>
            <Space>
              <Text>{selectedRowKeys.length} vendors selected</Text>
              <Button size="small">Activate</Button>
              <Button size="small">Deactivate</Button>
              <Button size="small" danger>Delete</Button>
            </Space>
          </div>
        )}

        {/* Table */}
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          rowSelection={rowSelection}
          scroll={{ x: 1300 }}
          pagination={{
            total: filteredData.length,
            //pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: [10, 20, 50, 100, 500],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} vendors`,
          }}
        />
      </Card>

      {/* Vendor Details Modal */}
      <Modal
        title={`Vendor Profile - ${selectedVendor?.name}`}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        width={1000}
        footer={[
          <Button key="close" onClick={() => setIsModalVisible(false)}>
            Close
          </Button>,
          <Button key="edit" type="primary" onClick={() => selectedVendor && handleAction(selectedVendor, 'edit')}>
            Edit Vendor
          </Button>,
        ]}
      >
        {selectedVendor && (
          <Tabs defaultActiveKey="info">
            <TabPane tab="Basic Information" key="info">
              <Descriptions column={2} bordered>
                <Descriptions.Item label="Vendor Code">{selectedVendor.code}</Descriptions.Item>
                <Descriptions.Item label="Name">{selectedVendor.name}</Descriptions.Item>
                <Descriptions.Item label="Type">
                  <Tag color={getTypeColor(selectedVendor.type)}>{selectedVendor.type.replace('_', ' ')}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Category">
                  <Tag color={getCategoryColor(selectedVendor.category)}>{selectedVendor.category.replace('_', ' ')}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Badge status={getStatusColor(selectedVendor.status) as any} text={selectedVendor.status.replace('_', ' ')} />
                </Descriptions.Item>
                <Descriptions.Item label="Payment Terms">{selectedVendor.paymentTerms.description}</Descriptions.Item>
                <Descriptions.Item label="Tax ID">{selectedVendor.taxInfo.taxId}</Descriptions.Item>
                <Descriptions.Item label="Created Date">{new Date(selectedVendor.createdAt).toLocaleDateString()}</Descriptions.Item>
              </Descriptions>
            </TabPane>
            
            <TabPane tab="Contact Information" key="contact">
              <Row gutter={16}>
                <Col span={12}>
                  <Card title="Primary Contact" size="small">
                    <Space direction="vertical">
                      <Text><strong>Name:</strong> {selectedVendor.contactInfo.primaryContact.name}</Text>
                      <Text><strong>Title:</strong> {selectedVendor.contactInfo.primaryContact.title}</Text>
                      <Text><strong>Email:</strong> {selectedVendor.contactInfo.primaryContact.email}</Text>
                      <Text><strong>Phone:</strong> {selectedVendor.contactInfo.primaryContact.phone}</Text>
                    </Space>
                  </Card>
                </Col>
                {selectedVendor.contactInfo.alternateContact && (
                  <Col span={12}>
                    <Card title="Alternate Contact" size="small">
                      <Space direction="vertical">
                        <Text><strong>Name:</strong> {selectedVendor.contactInfo.alternateContact.name}</Text>
                        <Text><strong>Title:</strong> {selectedVendor.contactInfo.alternateContact.title}</Text>
                        <Text><strong>Email:</strong> {selectedVendor.contactInfo.alternateContact.email}</Text>
                        <Text><strong>Phone:</strong> {selectedVendor.contactInfo.alternateContact.phone}</Text>
                      </Space>
                    </Card>
                  </Col>
                )}
              </Row>
            </TabPane>
            
            <TabPane tab="Performance & Rating" key="performance">
              <Row gutter={16}>
                <Col span={12}>
                  <Card title="Overall Rating" size="small">
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div>
                        <Text>Overall: </Text>
                        <Rate disabled value={selectedVendor.rating.overall} style={{ marginLeft: 8 }} />
                        <Text style={{ marginLeft: 8 }}>({selectedVendor.rating.overall}/5)</Text>
                      </div>
                      <div>
                        <Text>Quality: </Text>
                        <Rate disabled value={selectedVendor.rating.quality} style={{ marginLeft: 8 }} />
                      </div>
                      <div>
                        <Text>Delivery: </Text>
                        <Rate disabled value={selectedVendor.rating.delivery} style={{ marginLeft: 8 }} />
                      </div>
                      <div>
                        <Text>Service: </Text>
                        <Rate disabled value={selectedVendor.rating.service} style={{ marginLeft: 8 }} />
                      </div>
                      <div>
                        <Text>Price: </Text>
                        <Rate disabled value={selectedVendor.rating.price} style={{ marginLeft: 8 }} />
                      </div>
                    </Space>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card title="Performance Metrics" size="small">
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Statistic title="Total Orders" value={selectedVendor.rating.totalOrders} />
                      <div>
                        <Text>On-time Delivery: {selectedVendor.rating.onTimeDelivery}%</Text>
                        <Progress percent={selectedVendor.rating.onTimeDelivery} size="small" />
                      </div>
                      <Text type="secondary">Last evaluated: {new Date(selectedVendor.rating.lastEvaluated).toLocaleDateString()}</Text>
                    </Space>
                  </Card>
                </Col>
              </Row>
            </TabPane>
          </Tabs>
        )}
      </Modal>
    </div>
  );
}; 